import { MenuItem, Select as SelectMUI } from '@material-ui/core'
import React, { MutableRefObject, ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import uuid from 'uuid-random'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { stripHTML } from '../../../../../NonReact/utils/HTMLUtils'
import {
    Controller,
    ControllerProps,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CommonFormContext, FormContextType } from '../../../Contexts/CommonFormContext'
import { ReactState, Rules } from '../../../Types/Types'
import { $debug } from '../../../Utils/ConsoleUtils'
import Label from '../../Label'
import PopperBubble from '../../PopperBubble'

type SelectOptionValues = string | number

export type SelectOption<T extends SelectOptionValues> = {
    label: string
    value: T
    renderOptionElement?: ReactElement
    className?: string
    defaultOption?: boolean
}

export type SelectOptions<T extends SelectOptionValues> = SelectOption<T>[]

export type SelectProps<T extends SelectOptionValues> = {
    name?: string
    rules?: Rules
    label?: ReactNode
    labelClass?: string
    defaultValue?: T | undefined
    options: SelectOptions<T>
    disabled?: boolean
    renderSelectedValue?: boolean
    transform?: (value: unknown) => T
    value?: T | undefined
    handleChange?: (value: T) => void
    defaultTo?: number
    nativeTransitionIndex?: number
    noChange?: boolean
    iconComponent?: ReactElement
    classNames?: string
    onOpen?: () => void
    onKeyDown?: (event: React.KeyboardEvent<Element>) => void
    guardFunction?: (newValue: T) => Promise<Definable<boolean>>
    tooltip?: string
    disableTooltip?: boolean
    containerClassName?: string
    variant?: 'standard' | 'outlined' | 'filled'
    popoverClassName?: string
}
/**
 * @param selectName The name of the field - used for hooks forms fields.
 * @param label
 * @param labelClass - Custom class for the label.
 * @param rules Validation rules for the field.
 * @param defaultValue Selected default value
 * @param options {name: value}
 * @param disabled A flag defining the disabled state of the select component.
 * @param renderSelectedValue A flag specifying that selected option value should be rendered instead of selected option label.
 * @param nativeTransitionIndex This is the options array length at which the transition to native occurs.
 * @param handleChange Change handeler function.
 * @param selectValue value of the select.
 * @param transform Transform data function
 * @param defaultTo an index to default to.
 * @param noChange disbaled state with no styling changes
 * @param onKeyDown key down handler
 * @param guardFunction - Function to determine whether update should happen or not.
 * @param tooltip - Helper text
 * @param disableTooltip - Disables the tooltip
 * @param containerClassNames - Custom class for the select component.
 * @param variant - Material UI variant.
 */
const Select = <T extends SelectOptionValues>({
    name: selectName,
    label,
    labelClass,
    rules,
    defaultValue,
    options,
    disabled,
    renderSelectedValue,
    nativeTransitionIndex,
    handleChange,
    value: selectValue,
    transform,
    defaultTo,
    noChange,
    iconComponent,
    onKeyDown,
    classNames,
    onOpen,
    guardFunction,
    tooltip,
    disableTooltip,
    containerClassName,
    variant,
    popoverClassName
}: SelectProps<T>): ReactElement => {
    // eslint-disable-next-line
    let methods: Definable<UseFormMethods<Record<string, any>>>

    const [selectPopperOpen, setSelectPopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const selectPopperRef: MutableRefObject<null> = useRef(null)

    if (selectName) {
        methods = useFormContext()
    }

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const id: MutableRefObject<string> = useRef(uuid())

    // Transition to native select behaviour, defaulted to 200.
    const isNative: boolean = options.length > (nativeTransitionIndex ?? 200)

    // Define custom renderValue function in case we need to override renderValue prop of Select component.
    const renderValue = renderSelectedValue
        ? (val: unknown): string => {
              const selectedOption = options.find((option) => option.value === val)
              if (selectedOption) return String(selectedOption.value)
              return ''
          }
        : undefined
    const renderedOptions: ReactElement[] = options.map((option: SelectOption<T>, index) => {
        if (isNative) {
            return (
                <option className={option.className} value={option.value} key={option.value}>
                    {stripHTML(option.label)}
                </option>
            )
        }
        return (
            <MenuItem className={option.className} value={option.value} key={`${option.value} ${index}`}>
                {option.renderOptionElement ? (
                    option.renderOptionElement
                ) : (
                    <span className='option-wrapper' dangerouslySetInnerHTML={{ __html: option.label }} />
                )}
            </MenuItem>
        )
    })

    const firstOptionValue: T | string | undefined = options && options.length > 0 ? options[0].value : ''
    const calculatedDefaultValue: T | string | undefined = options.some((option) => option.value === defaultValue)
        ? defaultValue
        : firstOptionValue

    useEffect((): void => {
        // eslint-disable-next-line
        if (selectName && !IsVarEmpty(defaultTo) && options && options.length > defaultTo) {
            methods?.setValue(selectName, options[defaultTo].value)
        }
    }, [defaultTo, options])

    let controlledSelect

    if (methods) {
        const props: ControllerProps<typeof SelectMUI> = {
            control: methods.control,
            name: selectName ?? '',
            rules,
            defaultValue: methods?.watch(selectName ?? '', calculatedDefaultValue),
            onFocus: (): void => {
                const inputEl: Nullable<HTMLInputElement> = document.querySelector(
                    `input[name="${selectName}"]`
                ) as HTMLInputElement

                inputEl?.focus()
            },
            render: ({
                onChange,
                onBlur,
                value,
                name,
                ref
            }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                <SelectMUI
                    className={classNames}
                    name={name}
                    inputRef={ref}
                    disabled={!!disabled || noChange}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    value={value}
                    renderValue={renderValue}
                    onChange={(
                        event: React.ChangeEvent<{
                            name?: string
                            value: unknown
                        }>
                    ): void => {
                        const inputValue: T = event.target.value as T
                        if (commonForm?.handleFormChange) {
                            commonForm?.handleFormChange(name)
                        }

                        const runOnChange = (): void => {
                            onChange(inputValue)

                            if (handleChange) {
                                handleChange(transform ? transform(inputValue) : inputValue)
                            }
                        }

                        if (isVarNotEmpty(guardFunction)) {
                            guardFunction(inputValue)
                                .then((): void => {
                                    runOnChange()
                                })
                                .catch($debug)
                        } else {
                            runOnChange()
                        }
                    }}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    native={isNative}
                    MenuProps={{
                        className: popoverClassName,
                        getContentAnchorEl: null,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                        }
                    }}
                    IconComponent={iconComponent && ((): ReactElement => iconComponent)}
                    onOpen={onOpen}
                    variant={variant}
                >
                    {renderedOptions}
                </SelectMUI>
            )
        }
        controlledSelect = <Controller {...props} />
    }

    const uncontrolledSelect: ReactElement = (
        <SelectMUI
            variant={variant}
            onOpen={onOpen}
            className={classNames}
            disabled={!!disabled}
            value={selectValue}
            renderValue={renderValue}
            onChange={(
                event: React.ChangeEvent<{
                    name?: string | undefined
                    value: unknown
                }>
            ): void => {
                if (handleChange) {
                    handleChange(event.target.value as T)
                }
            }}
            native={isNative}
            IconComponent={iconComponent && ((): ReactElement => iconComponent)}
            onKeyDown={onKeyDown}
            MenuProps={{
                className: 'select-menu'
            }}
            inputProps={{
                id: id.current
            }}
        >
            {renderedOptions}
        </SelectMUI>
    )

    return (
        <div className={`select-wrapper ${noChange ? 'no-change' : ''} ${containerClassName}`}>
            <div className='select-input'>
                {label && (
                    <Label customClass={labelClass} htmlFor={id.current}>
                        {label}
                    </Label>
                )}
                <div
                    onMouseOver={(): void => setSelectPopperOpen(true)}
                    onMouseOut={(): void => setSelectPopperOpen(false)}
                    ref={selectPopperRef}
                    className='input-wrapper'
                >
                    {selectName ? controlledSelect : uncontrolledSelect}
                </div>
                <PopperBubble
                    content={<>{tooltip}</>}
                    open={
                        selectPopperOpen && !IsStringEmpty(tooltip) && isVarNotEmpty(disableTooltip) && !disableTooltip
                    }
                    anchorEl={selectPopperRef.current}
                    infoMode
                    placement='top'
                />
            </div>
        </div>
    )
}
export default Select
